import { forgetInvitationToken, invitationIdForExchange, invitationTokenForExchange } from './invitationContext';

export interface InvitationCourse { title?: string; slug?: string; href?: string }
export interface InvitationPreview {
  status: 'ready' | 'unavailable' | 'expired' | 'revoked';
  email?: string;
  course?: InvitationCourse;
  expiresAt?: string;
  contextExpiresAt?: string;
  invitationExpiresAt?: string;
  acceptanceStatus?: string;
  reason?: 'grant_expired' | 'context_expired' | 'invitation_expired';
  userExists?: boolean;
  accountSetupStatus?: 'not_started' | 'ready' | 'unconfirmed';
}

export class AccountSetupUnconfirmedError extends Error {
  constructor() { super('Account setup was not confirmed. Continue with normal sign-in or password recovery.'); }
}
export interface InvitationAcceptance {
  status: 'accepted' | 'active' | 'processing' | 'unknown' | 'email_unverified' | 'wrong_account' | 'expired' | 'revoked' | 'unavailable';
  authenticatedEmail?: string;
  course?: InvitationCourse;
  courseHref?: string;
  expiresAt?: string;
  reason?: 'grant_expired' | 'context_expired' | 'invitation_expired';
}

export function invitationSurfaceAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const api = new URL(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    if (api.username || api.password || api.search || api.hash || api.pathname !== '/') return false;
    if (window.location.origin === 'https://staging.hauraki.ai') return api.origin === 'https://staging-api.hauraki.ai';
    const local = (hostname: string) => ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
    return process.env.NODE_ENV !== 'production' && process.env.REACT_APP_HAURAKI_ENVIRONMENT === 'staging'
      && window.location.protocol === 'http:' && local(window.location.hostname) && api.protocol === 'http:' && local(api.hostname);
  } catch { return false; }
}

async function request(path: string, body: object, accessToken?: string): Promise<Record<string, unknown>> {
  if (!invitationSurfaceAllowed()) throw new Error('Invitation setup is available only on Hauraki staging.');
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 30_000);
  let response: Response;
  let data: unknown;
  try {
    response = await fetch(`${base}/course-tester-invitations/${path}`, {
      method: 'POST', credentials: 'include', cache: 'no-store', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify(body),
    });
    data = await response.json();
  } catch (error) {
    // A lost activation response cannot prove that the password setup did not
    // happen. Do not invite a second password submission after transport loss.
    if (path === 'activate') throw new AccountSetupUnconfirmedError();
    throw error;
  } finally { window.clearTimeout(timer); }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    if (path === 'activate') throw new AccountSetupUnconfirmedError();
    throw new Error('Invitation check could not be completed.');
  }
  const value = data as Record<string, unknown>;
  // Consume only bounded contract codes. Never surface a provider error/body.
  if (!response.ok && path === 'activate' && value.error === 'account_setup_unconfirmed') throw new AccountSetupUnconfirmedError();
  if (!response.ok && path !== 'accept') throw new Error('Invitation setup could not be completed. Try again.');
  if (!response.ok && path === 'accept') {
    if (value.error === 'invitation_acceptance_retryable') throw new Error('Invitation acceptance could not be completed. You can safely retry.');
    const states: Record<string, InvitationAcceptance['status']> = {
      wrong_account: 'wrong_account', email_verification_required: 'email_unverified',
      invitation_unavailable: 'unavailable', grant_expired: 'expired', grant_revoked: 'revoked',
      invitation_processing: 'processing', invitation_context_expired: 'expired', invitation_expired: 'expired',
    };
    const status = typeof value.error === 'string' ? states[value.error] : undefined;
    if (!status) throw new Error('Course access could not be checked. Try again.');
    const reason = value.error === 'invitation_context_expired' ? 'context_expired' : value.error === 'invitation_expired' ? 'invitation_expired' : value.error === 'grant_expired' ? 'grant_expired' : undefined;
    return { status, ...(reason ? { reason } : {}), ...(typeof value.authenticatedEmail === 'string' ? { authenticatedEmail: value.authenticatedEmail } : {}) };
  }
  return value;
}

let previewExchange: Promise<InvitationPreview> | null = null;
export function previewInvitation(): Promise<InvitationPreview> {
  // StrictMode / interrupted rendering must not exchange the same bearer twice.
  if (previewExchange) return previewExchange;
  const token = invitationTokenForExchange();
  previewExchange = request('preview', token ? { token, invitationId: invitationIdForExchange() } : {}).then((data) => {
    const statuses = ['ready', 'unavailable', 'expired', 'revoked'];
    if (!statuses.includes(String(data.status))) throw new Error('Invitation check could not be completed.');
    forgetInvitationToken(); // The server owns resumable HttpOnly cookie context.
    return data as unknown as InvitationPreview;
  }).finally(() => { previewExchange = null; });
  return previewExchange;
}

export async function activateInvitationAccount(password: string): Promise<void> {
  const data = await request('activate', { password });
  if (data.status !== 'account_ready') throw new AccountSetupUnconfirmedError();
}
export async function acceptInvitation(accessToken: string): Promise<InvitationAcceptance> {
  const data = await request('accept', {}, accessToken);
  if (!['accepted', 'active', 'processing', 'unknown', 'email_unverified', 'wrong_account', 'expired', 'revoked', 'unavailable'].includes(String(data.status))) {
    throw new Error('Course access could not be checked. Try again.');
  }
  return data as unknown as InvitationAcceptance;
}

/** Navigation is narrower than returnTo: only the server-confirmed Course 1. */
export function acceptedCourseHref(value: InvitationAcceptance | null): string | null {
  if (!value || !['accepted', 'active', 'unknown'].includes(value.status)) return null;
  const href = value.courseHref ?? value.course?.href;
  return href === '/portal/course/course-1-python-for-ai' ? href : null;
}
